"use client";
import React from 'react';
import { useCopilotAction } from "@copilotkit/react-core";
import { LoadingView } from '../ui/CalendarMeetingCard';
import { Calendar, MapPin, Users, Clock, Check, AlertCircle, User, Building, Search, Coffee, Wifi, Monitor } from 'lucide-react';
// Mock colleagues database
const mockColleagues = [
    {
        id: 'c1',
        name: 'Ngoc Nguyen',
        email: 'ngoc.nguyen@company.com',
        department: 'Engineering',
        jobTitle: 'Senior Software Engineer',
        status: 'available',
        preferredWorkingHours: { start: '09:00', end: '17:30' }
    },
    {
        id: 'c2',
        name: 'Mai Tran',
        email: 'mai.tran@company.com',
        department: 'Product',
        jobTitle: 'Product Manager',
        status: 'busy',
        preferredWorkingHours: { start: '08:30', end: '17:00' }
    },
    {
        id: 'c3',
        name: 'Duc Le',
        email: 'duc.le@company.com',
        department: 'Engineering',
        jobTitle: 'DevOps Engineer',
        status: 'available',
        preferredWorkingHours: { start: '08:00', end: '16:30' }
    },
    {
        id: 'c4',
        name: 'Linh Pham',
        email: 'linh.pham@company.com',
        department: 'Design',
        jobTitle: 'UX Designer',
        status: 'away',
        preferredWorkingHours: { start: '09:30', end: '18:00' }
    },
    {
        id: 'c5',
        name: 'Huy Vo',
        email: 'huy.vo@company.com',
        department: 'Marketing',
        jobTitle: 'Marketing Specialist',
        status: 'available',
        preferredWorkingHours: { start: '09:00', end: '17:00' }
    }
];
// Mock workspace locations
const mockLocations = [
    {
        id: 'loc1',
        name: 'Engineering Hub',
        building: 'Tower A',
        floor: 15,
        capacity: 40,
        amenities: ['WiFi', 'Monitors', 'Coffee Station', 'Whiteboard'],
        type: 'open_space'
    },
    {
        id: 'loc2',
        name: 'Product Suite',
        building: 'Tower A',
        floor: 12,
        capacity: 20,
        amenities: ['WiFi', 'Meeting Pods', 'Coffee Station'],
        type: 'office'
    },
    {
        id: 'loc3',
        name: 'Creative Studio',
        building: 'Tower B',
        floor: 8,
        capacity: 15,
        amenities: ['WiFi', 'Design Tools', 'Natural Light'],
        type: 'open_space'
    },
    {
        id: 'loc4',
        name: 'Hot Desk Zone',
        building: 'Tower A',
        floor: 10,
        capacity: 60,
        amenities: ['WiFi', 'Monitors', 'Phone Booths'],
        type: 'hot_desk'
    }
];
// Helper function to generate next 5 days
const getNext5Days = () => {
    const days = [];
    for (let i = 0; i < 5; i++) {
        const date = new Date();
        date.setDate(date.getDate() + i);
        days.push(date.toISOString().split('T')[0]);
    }
    return days;
};
// Helper function to generate mock schedule
const generateColleagueSchedule = (colleague, dates) => {
    return dates.map(date => {
        // Randomly assign location based on department
        let location = null;
        switch (colleague.department) {
            case 'Engineering':
                location = Math.random() > 0.3 ? mockLocations[0] : mockLocations[3]; // Engineering Hub or Hot Desk
                break;
            case 'Product':
                location = Math.random() > 0.2 ? mockLocations[1] : mockLocations[3]; // Product Suite or Hot Desk
                break;
            case 'Design':
                location = Math.random() > 0.4 ? mockLocations[2] : mockLocations[3]; // Creative Studio or Hot Desk
                break;
            default:
                location = mockLocations[3]; // Hot Desk Zone
        }
        // Generate time slots
        const timeSlots = [
            {
                start: '09:00',
                end: '12:00',
                status: (Math.random() > 0.7 ? 'tentative' : 'confirmed'),
                activity: Math.random() > 0.5 ? 'Development Work' : 'Team Meeting'
            },
            {
                start: '13:00',
                end: '17:00',
                status: (Math.random() > 0.6 ? 'tentative' : 'confirmed'),
                activity: Math.random() > 0.5 ? 'Project Review' : 'Focused Work'
            }
        ];
        return {
            date,
            location,
            timeSlots
        };
    });
};
// Helper function to format date
const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric'
    });
};
// Helper function to get available seats near colleague
const getAvailableSeatsNear = (colleague, location, date) => {
    const seatNumbers = ['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'C1', 'C2'];
    return seatNumbers.slice(0, Math.floor(Math.random() * 4) + 2).map((seatNum, index) => ({
        id: `seat-${location.id}-${seatNum}-${date}`,
        seatNumber: seatNum,
        location,
        date,
        timeSlot: {
            start: '09:00',
            end: '17:00'
        },
        nearColleagues: [colleague.name],
        price: location.type === 'hot_desk' ? 25 : 35,
        amenities: location.amenities
    }));
};
export function ColleaguesOutlook() {
    const [foundColleagues, setFoundColleagues] = React.useState([]);
    const [colleagueSchedule, setColleagueSchedule] = React.useState([]);
    const [availableSeats, setAvailableSeats] = React.useState([]);
    const [currentBooking, setCurrentBooking] = React.useState(null);
    // Get current date and time
    useCopilotAction({
        name: "getCurrentDateTime",
        description: "Get current date and time to help with date-based colleague and seat booking operations",
        parameters: [],
        handler: async () => {
            try {
                const now = new Date();
                const currentDate = now.toISOString().split('T')[0]; // YYYY-MM-DD
                const currentTime = now.toTimeString().split(' ')[0].substring(0, 5); // HH:MM
                const next5Days = getNext5Days();
                return {
                    _success: true,
                    currentDate,
                    currentTime,
                    currentDateTime: now.toISOString(),
                    next5Days,
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    _message: `Current date: ${currentDate}, Time: ${currentTime}. Next 5 days available: ${next5Days.join(', ')}`
                };
            }
            catch (error) {
                return {
                    _error: true,
                    _message: `Failed to get current date time: ${error instanceof Error ? error.message : String(error)}`
                };
            }
        },
        render: ({ status, result }) => {
            if (status === "executing") {
                return React.createElement(LoadingView, null);
            }
            if (status === "complete" && result) {
                if (result._error) {
                    return (React.createElement("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(AlertCircle, { className: "w-5 h-5 text-red-600" }),
                            React.createElement("p", { className: "text-red-800" }, result._message))));
                }
                if (result._success) {
                    return (React.createElement("div", { className: "bg-gray-50 border border-gray-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2 mb-3" },
                            React.createElement(Clock, { className: "w-5 h-5 text-gray-600" }),
                            React.createElement("h4", { className: "font-medium text-gray-900" }, "Current Date & Time")),
                        React.createElement("div", { className: "space-y-2 text-sm text-gray-700" },
                            React.createElement("p", null,
                                React.createElement("strong", null, "Date:"),
                                " ",
                                result.currentDate),
                            React.createElement("p", null,
                                React.createElement("strong", null, "Time:"),
                                " ",
                                result.currentTime),
                            React.createElement("p", null,
                                React.createElement("strong", null, "Timezone:"),
                                " ",
                                result.timeZone),
                            React.createElement("div", { className: "mt-3" },
                                React.createElement("p", null,
                                    React.createElement("strong", null, "Next 5 Available Days:")),
                                React.createElement("div", { className: "flex flex-wrap gap-2 mt-1" }, result.next5Days.map((date, index) => (React.createElement("span", { key: index, className: "px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs" }, formatDate(date)))))))));
                }
            }
            return React.createElement("div", null);
        },
    });
    // Find colleagues by name
    useCopilotAction({
        name: "findColleagues",
        description: "Search for colleagues by name or department",
        parameters: [
            {
                name: "searchQuery",
                type: "string",
                description: "Name or department to search for (e.g., 'Ngoc', 'Engineering', etc.)",
                required: true,
            },
        ],
        handler: async ({ searchQuery }) => {
            try {
                const query = searchQuery.toLowerCase();
                const matches = mockColleagues.filter(colleague => colleague.name.toLowerCase().includes(query) ||
                    colleague.department.toLowerCase().includes(query) ||
                    colleague.jobTitle.toLowerCase().includes(query));
                setFoundColleagues(matches);
                return {
                    _success: true,
                    colleagues: matches,
                    totalFound: matches.length,
                    _message: `Found ${matches.length} colleague(s) matching "${searchQuery}"`
                };
            }
            catch (error) {
                return {
                    _error: true,
                    _message: `Failed to search colleagues: ${error instanceof Error ? error.message : String(error)}`
                };
            }
        },
        render: ({ status, result }) => {
            if (status === "executing") {
                return React.createElement(LoadingView, null);
            }
            if (status === "complete" && result) {
                if (result._error) {
                    return (React.createElement("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(AlertCircle, { className: "w-5 h-5 text-red-600" }),
                            React.createElement("p", { className: "text-red-800" }, result._message))));
                }
                if (result._success && result.colleagues) {
                    return (React.createElement("div", { className: "bg-white border border-gray-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2 mb-4" },
                            React.createElement(Search, { className: "w-5 h-5 text-blue-600" }),
                            React.createElement("h4", { className: "font-medium text-gray-900" },
                                "Found Colleagues (",
                                result.totalFound,
                                ")")),
                        React.createElement("div", { className: "space-y-3" }, result.colleagues.map((colleague) => (React.createElement("div", { key: colleague.id, className: "border border-gray-100 rounded-lg p-4" },
                            React.createElement("div", { className: "flex items-start justify-between" },
                                React.createElement("div", { className: "flex items-center space-x-3" },
                                    React.createElement("div", { className: "w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" },
                                        React.createElement(User, { className: "w-6 h-6 text-blue-600" })),
                                    React.createElement("div", null,
                                        React.createElement("h5", { className: "font-medium text-gray-900" }, colleague.name),
                                        React.createElement("p", { className: "text-sm text-gray-600" }, colleague.jobTitle),
                                        React.createElement("p", { className: "text-sm text-gray-500" }, colleague.department))),
                                React.createElement("div", { className: "text-right" },
                                    React.createElement("span", { className: `px-2 py-1 rounded-full text-xs ${colleague.status === 'available' ? 'bg-green-100 text-green-800' :
                                            colleague.status === 'busy' ? 'bg-red-100 text-red-800' :
                                                colleague.status === 'away' ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-gray-100 text-gray-800'}` }, colleague.status.toUpperCase()),
                                    React.createElement("p", { className: "text-xs text-gray-500 mt-1" },
                                        colleague.preferredWorkingHours.start,
                                        " - ",
                                        colleague.preferredWorkingHours.end))),
                            React.createElement("div", { className: "mt-3 pt-3 border-t border-gray-100" },
                                React.createElement("p", { className: "text-sm text-gray-600" },
                                    React.createElement("strong", null, "Email:"),
                                    " ",
                                    colleague.email))))))));
                }
            }
            return React.createElement("div", null);
        },
    });
    // Get colleague's location schedule for next 5 days
    useCopilotAction({
        name: "getColleagueSchedule",
        description: "Get a colleague's location and schedule for the next 5 days",
        parameters: [
            {
                name: "colleagueName",
                type: "string",
                description: "Name of the colleague to check schedule for",
                required: true,
            },
        ],
        handler: async ({ colleagueName }) => {
            try {
                const colleague = mockColleagues.find(c => c.name.toLowerCase().includes(colleagueName.toLowerCase()));
                if (!colleague) {
                    return {
                        _error: true,
                        _message: `Colleague "${colleagueName}" not found. Try searching first.`
                    };
                }
                const next5Days = getNext5Days();
                const schedule = generateColleagueSchedule(colleague, next5Days);
                setColleagueSchedule(schedule);
                return {
                    _success: true,
                    colleague,
                    schedule,
                    dates: next5Days,
                    _message: `Retrieved schedule for ${colleague.name} for the next 5 days`
                };
            }
            catch (error) {
                return {
                    _error: true,
                    _message: `Failed to get schedule: ${error instanceof Error ? error.message : String(error)}`
                };
            }
        },
        render: ({ status, result }) => {
            if (status === "executing") {
                return React.createElement(LoadingView, null);
            }
            if (status === "complete" && result) {
                if (result._error) {
                    return (React.createElement("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(AlertCircle, { className: "w-5 h-5 text-red-600" }),
                            React.createElement("p", { className: "text-red-800" }, result._message))));
                }
                if (result._success && result.colleague && result.schedule) {
                    const colleague = result.colleague;
                    const schedule = result.schedule;
                    return (React.createElement("div", { className: "bg-blue-50 border border-blue-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2 mb-4" },
                            React.createElement(Calendar, { className: "w-5 h-5 text-blue-600" }),
                            React.createElement("h4", { className: "font-medium text-blue-900" },
                                colleague.name,
                                "'s Schedule (Next 5 Days)")),
                        React.createElement("div", { className: "space-y-3" }, schedule.map((daySchedule, index) => (React.createElement("div", { key: index, className: "bg-white rounded-lg p-4 border border-blue-100" },
                            React.createElement("div", { className: "flex justify-between items-start mb-3" },
                                React.createElement("div", null,
                                    React.createElement("h5", { className: "font-medium text-gray-900" }, formatDate(daySchedule.date)),
                                    daySchedule.location && (React.createElement("div", { className: "flex items-center space-x-2 mt-1" },
                                        React.createElement(Building, { className: "w-4 h-4 text-gray-500" }),
                                        React.createElement("span", { className: "text-sm text-gray-600" },
                                            daySchedule.location.name,
                                            " - ",
                                            daySchedule.location.building,
                                            ", Floor ",
                                            daySchedule.location.floor)))),
                                daySchedule.location && (React.createElement("span", { className: "text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full" }, daySchedule.location.type.replace('_', ' ').toUpperCase()))),
                            daySchedule.location ? (React.createElement(React.Fragment, null,
                                React.createElement("div", { className: "space-y-2 mb-3" }, daySchedule.timeSlots.map((slot, slotIndex) => (React.createElement("div", { key: slotIndex, className: "flex justify-between items-center text-sm" },
                                    React.createElement("div", { className: "flex items-center space-x-2" },
                                        React.createElement(Clock, { className: "w-4 h-4 text-gray-500" }),
                                        React.createElement("span", null,
                                            slot.start,
                                            " - ",
                                            slot.end),
                                        React.createElement("span", { className: `px-2 py-1 rounded-full text-xs ${slot.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                                slot.status === 'tentative' ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-gray-100 text-gray-800'}` }, slot.status)),
                                    slot.activity && (React.createElement("span", { className: "text-gray-600" }, slot.activity)))))),
                                React.createElement("div", { className: "border-t border-gray-100 pt-3" },
                                    React.createElement("div", { className: "flex flex-wrap gap-2" }, daySchedule.location.amenities.map((amenity, amenityIndex) => (React.createElement("span", { key: amenityIndex, className: "text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full flex items-center space-x-1" },
                                        amenity === 'WiFi' && React.createElement(Wifi, { className: "w-3 h-3" }),
                                        amenity === 'Monitors' && React.createElement(Monitor, { className: "w-3 h-3" }),
                                        amenity === 'Coffee Station' && React.createElement(Coffee, { className: "w-3 h-3" }),
                                        React.createElement("span", null, amenity)))))))) : (React.createElement("div", { className: "text-center py-4" },
                                React.createElement("p", { className: "text-gray-500" }, "Working from home")))))))));
                }
            }
            return React.createElement("div", null);
        },
    });
    // Find available seats near colleague
    useCopilotAction({
        name: "findSeatsNearColleague",
        description: "Find available seats near a specific colleague for a given date",
        parameters: [
            {
                name: "colleagueName",
                type: "string",
                description: "Name of the colleague you want to sit near",
                required: true,
            },
            {
                name: "date",
                type: "string",
                description: "Date in YYYY-MM-DD format (use getCurrentDateTime to get current date first)",
                required: true,
            },
        ],
        handler: async ({ colleagueName, date }) => {
            try {
                const colleague = mockColleagues.find(c => c.name.toLowerCase().includes(colleagueName.toLowerCase()));
                if (!colleague) {
                    return {
                        _error: true,
                        _message: `Colleague "${colleagueName}" not found. Try searching first.`
                    };
                }
                // Get colleague's location for that date
                const next5Days = getNext5Days();
                if (!next5Days.includes(date)) {
                    return {
                        _error: true,
                        _message: `Date ${date} is not within the next 5 days. Please select a date between ${next5Days[0]} and ${next5Days[4]}.`
                    };
                }
                const scheduleForDate = generateColleagueSchedule(colleague, [date])[0];
                if (!scheduleForDate.location) {
                    return {
                        _error: true,
                        _message: `${colleague.name} is working from home on ${date}. No office seats available.`
                    };
                }
                const availableSeats = getAvailableSeatsNear(colleague, scheduleForDate.location, date);
                setAvailableSeats(availableSeats);
                return {
                    _success: true,
                    colleague,
                    date,
                    location: scheduleForDate.location,
                    availableSeats,
                    totalSeats: availableSeats.length,
                    _message: `Found ${availableSeats.length} available seats near ${colleague.name} on ${formatDate(date)}`
                };
            }
            catch (error) {
                return {
                    _error: true,
                    _message: `Failed to find seats: ${error instanceof Error ? error.message : String(error)}`
                };
            }
        },
        render: ({ status, result }) => {
            if (status === "executing") {
                return React.createElement(LoadingView, null);
            }
            if (status === "complete" && result) {
                if (result._error) {
                    return (React.createElement("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(AlertCircle, { className: "w-5 h-5 text-red-600" }),
                            React.createElement("p", { className: "text-red-800" }, result._message))));
                }
                if (result._success && result.availableSeats) {
                    const colleague = result.colleague;
                    const location = result.location;
                    const seats = result.availableSeats;
                    return (React.createElement("div", { className: "bg-green-50 border border-green-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2 mb-4" },
                            React.createElement(MapPin, { className: "w-5 h-5 text-green-600" }),
                            React.createElement("h4", { className: "font-medium text-green-900" },
                                "Available Seats Near ",
                                colleague.name,
                                " - ",
                                formatDate(result.date))),
                        React.createElement("div", { className: "mb-4 p-3 bg-white rounded-lg border border-green-100" },
                            React.createElement("div", { className: "flex items-center space-x-2 mb-2" },
                                React.createElement(Building, { className: "w-4 h-4 text-gray-500" }),
                                React.createElement("span", { className: "font-medium" }, location.name)),
                            React.createElement("p", { className: "text-sm text-gray-600" },
                                location.building,
                                ", Floor ",
                                location.floor,
                                " \u2022 Capacity: ",
                                location.capacity,
                                " people"),
                            React.createElement("div", { className: "flex flex-wrap gap-2 mt-2" }, location.amenities.map((amenity, index) => (React.createElement("span", { key: index, className: "text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full" }, amenity))))),
                        React.createElement("div", { className: "space-y-3" },
                            React.createElement("h5", { className: "font-medium text-gray-900" },
                                "Available Seats (",
                                seats.length,
                                ")"),
                            seats.map((seat) => (React.createElement("div", { key: seat.id, className: "bg-white rounded-lg p-4 border border-green-100" },
                                React.createElement("div", { className: "flex justify-between items-start" },
                                    React.createElement("div", null,
                                        React.createElement("h6", { className: "font-medium text-gray-900" },
                                            "Seat ",
                                            seat.seatNumber),
                                        React.createElement("div", { className: "flex items-center space-x-2 mt-1" },
                                            React.createElement(Clock, { className: "w-4 h-4 text-gray-500" }),
                                            React.createElement("span", { className: "text-sm text-gray-600" },
                                                seat.timeSlot.start,
                                                " - ",
                                                seat.timeSlot.end)),
                                        React.createElement("div", { className: "flex items-center space-x-2 mt-1" },
                                            React.createElement(Users, { className: "w-4 h-4 text-gray-500" }),
                                            React.createElement("span", { className: "text-sm text-gray-600" },
                                                "Near: ",
                                                seat.nearColleagues.join(', ')))),
                                    React.createElement("div", { className: "text-right" },
                                        React.createElement("span", { className: "text-lg font-bold text-green-600" },
                                            "$",
                                            seat.price),
                                        React.createElement("p", { className: "text-xs text-gray-500" }, "per day"))),
                                React.createElement("div", { className: "mt-3 pt-3 border-t border-gray-100" },
                                    React.createElement("div", { className: "flex flex-wrap gap-2" }, seat.amenities.map((amenity, index) => (React.createElement("span", { key: index, className: "text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full" }, amenity)))))))))));
                }
            }
            return React.createElement("div", null);
        },
    });
    // Book a seat near colleague
    useCopilotAction({
        name: "bookSeatNearColleague",
        description: "Book a specific seat near a colleague",
        parameters: [
            {
                name: "seatNumber",
                type: "string",
                description: "Seat number to book (e.g., 'A1', 'B2')",
                required: true,
            },
            {
                name: "date",
                type: "string",
                description: "Date for the booking in YYYY-MM-DD format",
                required: true,
            },
            {
                name: "locationId",
                type: "string",
                description: "Location ID where the seat is located",
                required: true,
            },
        ],
        handler: async ({ seatNumber, date, locationId }) => {
            try {
                const location = mockLocations.find(loc => loc.id === locationId);
                if (!location) {
                    return {
                        _error: true,
                        _message: `Location not found: ${locationId}`
                    };
                }
                const seat = availableSeats.find(s => s.seatNumber === seatNumber &&
                    s.date === date &&
                    s.location.id === locationId);
                if (!seat) {
                    return {
                        _error: true,
                        _message: `Seat ${seatNumber} not found or not available for ${date}. Please search for available seats first.`
                    };
                }
                // Simulate booking
                const booking = Object.assign(Object.assign({}, seat), { id: `booking-${Date.now()}` });
                setCurrentBooking(booking);
                return {
                    _success: true,
                    booking,
                    confirmationNumber: booking.id.toUpperCase(),
                    _message: `Successfully booked seat ${seatNumber} near ${seat.nearColleagues.join(', ')} for ${formatDate(date)}`
                };
            }
            catch (error) {
                return {
                    _error: true,
                    _message: `Failed to book seat: ${error instanceof Error ? error.message : String(error)}`
                };
            }
        },
        render: ({ status, result }) => {
            if (status === "executing") {
                return React.createElement(LoadingView, null);
            }
            if (status === "complete" && result) {
                if (result._error) {
                    return (React.createElement("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(AlertCircle, { className: "w-5 h-5 text-red-600" }),
                            React.createElement("p", { className: "text-red-800" }, result._message))));
                }
                if (result._success && result.booking) {
                    const booking = result.booking;
                    return (React.createElement("div", { className: "bg-green-50 border border-green-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2 mb-4" },
                            React.createElement(Check, { className: "w-5 h-5 text-green-600" }),
                            React.createElement("h4", { className: "font-medium text-green-900" }, "\u2705 Seat Booking Confirmed!")),
                        React.createElement("div", { className: "bg-white rounded-lg p-4 border border-green-100" },
                            React.createElement("div", { className: "flex justify-between items-start mb-4" },
                                React.createElement("div", null,
                                    React.createElement("h5", { className: "font-medium text-gray-900" }, "Booking Details"),
                                    React.createElement("p", { className: "text-sm text-gray-600" },
                                        "Confirmation: ",
                                        result.confirmationNumber)),
                                React.createElement("span", { className: "text-2xl font-bold text-green-600" },
                                    "$",
                                    booking.price)),
                            React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" },
                                React.createElement("div", null,
                                    React.createElement("div", { className: "space-y-2 text-sm" },
                                        React.createElement("div", { className: "flex items-center space-x-2" },
                                            React.createElement(MapPin, { className: "w-4 h-4 text-gray-500" }),
                                            React.createElement("span", null,
                                                React.createElement("strong", null, "Seat:"),
                                                " ",
                                                booking.seatNumber)),
                                        React.createElement("div", { className: "flex items-center space-x-2" },
                                            React.createElement(Building, { className: "w-4 h-4 text-gray-500" }),
                                            React.createElement("span", null,
                                                React.createElement("strong", null, "Location:"),
                                                " ",
                                                booking.location.name)),
                                        React.createElement("div", { className: "flex items-center space-x-2" },
                                            React.createElement(Calendar, { className: "w-4 h-4 text-gray-500" }),
                                            React.createElement("span", null,
                                                React.createElement("strong", null, "Date:"),
                                                " ",
                                                formatDate(booking.date))),
                                        React.createElement("div", { className: "flex items-center space-x-2" },
                                            React.createElement(Clock, { className: "w-4 h-4 text-gray-500" }),
                                            React.createElement("span", null,
                                                React.createElement("strong", null, "Time:"),
                                                " ",
                                                booking.timeSlot.start,
                                                " - ",
                                                booking.timeSlot.end)))),
                                React.createElement("div", null,
                                    React.createElement("div", { className: "space-y-2 text-sm" },
                                        React.createElement("div", { className: "flex items-center space-x-2" },
                                            React.createElement(Users, { className: "w-4 h-4 text-gray-500" }),
                                            React.createElement("span", null,
                                                React.createElement("strong", null, "Near:"),
                                                " ",
                                                booking.nearColleagues.join(', '))),
                                        React.createElement("div", null,
                                            React.createElement("span", { className: "text-gray-700" },
                                                React.createElement("strong", null, "Address:")),
                                            React.createElement("p", { className: "text-gray-600" },
                                                booking.location.building,
                                                ", Floor ",
                                                booking.location.floor))))),
                            React.createElement("div", { className: "border-t border-gray-100 pt-3" },
                                React.createElement("h6", { className: "font-medium text-gray-900 mb-2" }, "Available Amenities"),
                                React.createElement("div", { className: "flex flex-wrap gap-2" }, booking.amenities.map((amenity, index) => (React.createElement("span", { key: index, className: "text-xs px-3 py-1 bg-blue-100 text-blue-800 rounded-full flex items-center space-x-1" },
                                    amenity === 'WiFi' && React.createElement(Wifi, { className: "w-3 h-3" }),
                                    amenity === 'Monitors' && React.createElement(Monitor, { className: "w-3 h-3" }),
                                    amenity === 'Coffee Station' && React.createElement(Coffee, { className: "w-3 h-3" }),
                                    React.createElement("span", null, amenity))))))),
                        React.createElement("div", { className: "mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" },
                            React.createElement("p", { className: "text-sm text-blue-800" },
                                React.createElement("strong", null, "Next Steps:"),
                                " Check your email for booking confirmation and QR code for office access."))));
                }
            }
            return React.createElement("div", null);
        },
    });
    return null; // This component doesn't render anything directly
}
